import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as React from 'react';
import { useAuth } from "react-oidc-context";
import API, { GenericPagedResponse } from '../common/API';
import { useTranslation } from 'react-i18next';

export interface PaginationProps {
    perPage: number;
    route: string;
    searchQuery?: (val: string) => string;
}

export function usePagination<T>(props: PaginationProps):
    [results: T[], pagination: () => JSX.Element, setCustomParams:React.Dispatch<React.SetStateAction<string|undefined>>, getResults: Function ] {
    const auth = useAuth();
    const [page, setPage] = React.useState<number>(1)
    const [perPage, setPerPage] = React.useState(props.perPage)
    const [pages, setPages] = React.useState<number[]>([1])
    const [customParams, setCustomParams] = React.useState<string>()
    const [results, setResults] = React.useState<T[]>([])
    const [totalRecords, setTotalRecords] = React.useState<number>(0)
    const { t } = useTranslation();

    if (props.searchQuery == null)
        props.searchQuery = (e) => 'Name.Contains("' + e + '")'

    React.useEffect(() => {
        getResults()
    }, [auth, page, perPage, customParams, props.route])

    const getResults = async () => {
        const params: URLSearchParams = new URLSearchParams()
        params.set("pageNumber", page.toString());
        params.set("perPage", perPage.toString());

        var url: string = props.route
        if (url.indexOf("?") === -1)
            url += "?";
        url += params.toString();
        if (customParams != null && customParams.length > 0)
            url += "&" + customParams

        const response: GenericPagedResponse<T> | null = await API.get(url, auth)
        if (response != null) {
            setPages(calculatePages(page, perPage, response.totalRecords));
            setResults(response.results);
            setTotalRecords(response.totalRecords);
        }
    }

    const calculatePages = (page: number, perPage: number, total: number): number[] => {
        const delta = 2;
        const left = page - delta;
        const right = page + delta + 1;
        const pageCount = Math.ceil(total / perPage);

        const results = Array.from({ length: pageCount }, (v, k) => k + 1)
            .filter(i => i && i >= left && i < right);

        return results;
    }

    const pagination = () => {
        return (
            <div className="d-flex justify-content-between align-items-center">
                {t('common:showingEntries', { start: ((page - 1) * perPage) + 1, end: ((page - 1) * perPage) + results.length, total: totalRecords }) }
                <ul className="pagination mb-0">
                    <li className={"page-item previous " + (page === 1 ? "disabled" : "")}><button type="button" className="page-link" onClick={function () { setPage(page - 1) }}>{t('common:previous')}</button></li>
                    {pages.map((e: number) => {
                        return (
                            <li className={"page-item " + (page === e ? "active" : "")} key={e}><button type="button" className="page-link" onClick={function () { setPage(e) }}>{e}</button></li>
                        )
                    })}
                    <li className={"page-item next " + (page === pages[pages.length - 1] ? "disabled" : "")}><button type="button" className="page-link" onClick={function () { setPage(page + 1) }}>{t('common:next')}</button></li>
                </ul>
            </div>
        )
    }

    return [results, pagination, setCustomParams, getResults]
}